import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import SpotMeLogo from 'src/components/SpotMeLogo'
import { RedirectUrls } from 'src/types/types'
import { hasAnyOnDemandCoverage } from 'src/utils/onDemand'
import ga from 'src/utils/pixels/gtag'
import FooterAccordion from './FooterAccordion'
import SocialMediaIcons from './SocialMediaIcons'
import {
  BLOG_LINKS,
  BUSINESS_USES_LINKS,
  type FooterTranslationKey,
  HELP_LINKS,
  type IItemProps,
  MONETIZE_LINKS,
  POPULAR_CITIES,
  STORAGE_TYPES_LINKS
} from './constants'

/**
 * Visibility gate for footer links. Returns `true` when the link should
 * render. Currently the only gate is `on-demand-coverage`, which hides the
 * Almacenamiento a Domicilio link when no city has `partnerActive: true`.
 */
const isFooterItemVisible = (item: IItemProps): boolean => {
  if (item.gateBy === 'on-demand-coverage') return hasAnyOnDemandCoverage()
  return true
}

// ---------------------------------------------------------------------------
// Theme tokens — editorial colophon in two tones.
// Dark (default): ink-800 navy, white-alpha text, blue-500 eyebrows (6.3:1).
// Light (search drawer, legal, directory): cream-300, ink text, and blue-800
// for coloured TEXT — blue-500 fails AA as text on cream (2.5:1), see
// docs/claude/design-language.md → "blue-500 fails WCAG AA as text".
// ---------------------------------------------------------------------------

interface FooterTheme {
  /** Footer surface + base text colour. */
  root: string
  /** Display type: tagline, numerals, CTA headline. */
  strong: string
  /** Navigational links. */
  link: string
  /** Secondary copy: stat labels, copyright, separators. */
  muted: string
  /** Eyebrow headings and small accents. */
  eyebrow: string
  /** Hairline colour (pair with a `border-*` width class). */
  rule: string
  /** Link hover colour + the underline colour hover reveals. */
  hover: string
  /** Focus-visible outline colour. */
  ring: string
  /** Contact glyphs. */
  icon: string
  /** Host CTA panel surface. */
  panel: string
  /** Decorative radial glow on the CTA panel ('' = none). */
  glow: string
  primaryCta: string
  secondaryCta: string
  /** Tint applied to the inline-SVG logo ('' = brand colours). */
  logoTint: string
  /** Text colour handed to SocialMediaIcons. */
  social: string
}

const DARK_THEME: FooterTheme = {
  root: 'bg-ink-800 text-white-500',
  strong: 'text-white-500',
  link: 'text-white-500/80',
  muted: 'text-white-500/60',
  eyebrow: 'text-blue-500',
  rule: 'border-white-500/15',
  hover: 'hover:text-white-500 decoration-blue-500',
  ring: 'focus-visible:outline-white-500',
  icon: 'text-blue-500',
  panel: 'border-white-500/15 bg-ink-700',
  glow: 'bg-[radial-gradient(circle,_rgba(0,164,255,0.22)_0%,_transparent_68%)]',
  primaryCta: 'bg-white-500 text-blue-800 hover:bg-cream-300',
  secondaryCta:
    'border-white-500/30 text-white-500 hover:border-white-500 hover:bg-white-500/10',
  logoTint: '[&_*]:fill-white-500',
  social: 'text-white-500'
}

const LIGHT_THEME: FooterTheme = {
  root: 'bg-cream-300 text-ink-700',
  strong: 'text-ink-700',
  link: 'text-ink-700',
  muted: 'text-ink-200',
  eyebrow: 'text-blue-800',
  rule: 'border-ink-100/15',
  hover: 'hover:text-blue-800 decoration-blue-800/40',
  ring: 'focus-visible:outline-ink-700',
  icon: 'text-blue-800',
  panel: 'border-ink-100/15 bg-white-500',
  glow: '',
  primaryCta: 'bg-blue-800 text-white-500 hover:bg-ink-700',
  secondaryCta:
    'border-ink-100/40 text-ink-700 hover:border-ink-700 hover:bg-cream-300',
  logoTint: '',
  social: 'text-ink-700'
}

/** Editorial eyebrow — the same small caps the mega menu and drawer use. */
const EYEBROW = 'font-display text-[11px] tracking-[0.22em] uppercase'

/** Focus ring shape; each call site adds the theme's `ring` colour. */
const FOCUS =
  'rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2'

type FooterSection = { titleKey: FooterTranslationKey; items: IItemProps[] }

/** Desktop: four columns, the first two stacking two sections each. */
const DESKTOP_COLUMNS: FooterSection[][] = [
  [
    { titleKey: 'FOOTER.STORAGE_TYPES', items: STORAGE_TYPES_LINKS },
    { titleKey: 'FOOTER.BUSINESS_USES', items: BUSINESS_USES_LINKS }
  ],
  [
    { titleKey: 'FOOTER.LOCATIONS', items: POPULAR_CITIES },
    { titleKey: 'FOOTER.MONETIZE', items: MONETIZE_LINKS }
  ],
  [{ titleKey: 'FOOTER.HELP', items: HELP_LINKS }],
  [{ titleKey: 'FOOTER.BLOG', items: BLOG_LINKS }]
]

/** Mobile: one accordion per section, renter-first order. */
const MOBILE_SECTIONS: FooterSection[] = [
  { titleKey: 'FOOTER.STORAGE_TYPES', items: STORAGE_TYPES_LINKS },
  { titleKey: 'FOOTER.LOCATIONS', items: POPULAR_CITIES },
  { titleKey: 'FOOTER.MONETIZE', items: MONETIZE_LINKS },
  { titleKey: 'FOOTER.BUSINESS_USES', items: BUSINESS_USES_LINKS },
  { titleKey: 'FOOTER.HELP', items: HELP_LINKS },
  { titleKey: 'FOOTER.BLOG', items: BLOG_LINKS }
]

const LEGAL_LINKS: { href: RedirectUrls; labelKey: FooterTranslationKey }[] = [
  { href: RedirectUrls.TermsAndConditions, labelKey: 'FOOTER.TERMS' },
  { href: RedirectUrls.Disclosures, labelKey: 'FOOTER.PRIVACY' },
  { href: RedirectUrls.Contact, labelKey: 'FOOTER.CONTACT' }
]

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

/**
 * Renders a list of translated footer links. Mobile rows are full-width 44px
 * tap targets inside the accordions; from `md` they collapse to inline text.
 */
function LinkList({
  items,
  theme
}: {
  items: IItemProps[]
  theme: FooterTheme
}) {
  const { t } = useTranslation('footer')
  return (
    <>
      {items.filter(isFooterItemVisible).map((item, i) => (
        <li key={i}>
          <Link
            href={item.href}
            prefetch={false}
            onClick={() =>
              ga.event('nav_link_click', { url: item.href, surface: 'footer' })
            }
            className={`flex min-h-11 items-center text-[15px] leading-snug decoration-1 underline-offset-5 transition-colors duration-200 hover:underline md:inline-flex md:min-h-0 md:py-1 md:text-sm ${theme.link} ${theme.hover} ${FOCUS} ${theme.ring}`}
          >
            {t(item.children)}
          </Link>
        </li>
      ))}
    </>
  )
}

/** Desktop link column — eyebrow heading on a hairline, then the list. */
function LinkColumn({
  section,
  theme
}: {
  section: FooterSection
  theme: FooterTheme
}) {
  const { t } = useTranslation('footer')
  return (
    <div>
      <h2 className={`border-b pb-3 ${EYEBROW} ${theme.rule} ${theme.eyebrow}`}>
        {t(section.titleKey)}
      </h2>
      <ul className="mt-3 space-y-1">
        <LinkList items={section.items} theme={theme} />
      </ul>
    </div>
  )
}

/** Single trust-bar stat — italic Fraunces numeral on a hairline rule. */
function TrustStat({
  value,
  label,
  theme
}: {
  value: string
  label: string
  theme: FooterTheme
}) {
  return (
    <div
      className={`flex min-w-0 flex-col border-l pl-2 sm:pl-3 md:pl-4 ${theme.rule}`}
    >
      {/* Fluid on phones: a fixed 28px overflowed a 3-tile row at 320px.
          The bar is 2 tiles since 2026-09-30 (only stats that read as
          scale — see docs/marketing/platform-stats.md). */}
      <span
        className={`font-display text-[clamp(18px,5.6vw,28px)] leading-none font-medium tracking-[-0.01em] whitespace-nowrap italic md:text-[34px] ${theme.strong}`}
      >
        {value}
      </span>
      <span
        className={`mt-2 text-[10px] tracking-[0.06em] uppercase sm:text-[11px] sm:tracking-[0.12em] ${theme.muted}`}
      >
        {label}
      </span>
    </div>
  )
}

/** Logo + tagline at display scale, social links on the right rail. */
function BrandZone({ theme }: { theme: FooterTheme }) {
  const { t } = useTranslation('footer')
  return (
    <div className="mx-auto max-w-7xl px-5 pt-12 pb-10 md:grid md:grid-cols-12 md:items-end md:gap-12 md:px-8 md:pt-16 md:pb-12">
      <div className="md:col-span-8">
        {/* Inline-SVG logo (hydration Pass 5 — docs/claude/hydration.md);
            the wrapper sizes it and, on navy, tints every path white. */}
        <div className={`[&_svg]:w-30 md:[&_svg]:w-35 ${theme.logoTint}`}>
          <SpotMeLogo />
        </div>
        <p
          className={`mt-6 max-w-2xl font-display text-[22px] leading-[1.2] font-medium tracking-[-0.01em] text-balance md:text-[30px] ${theme.strong}`}
        >
          {t('FOOTER.TAGLINE')}
        </p>
      </div>

      <div className="mt-8 md:col-span-4 md:mt-0 md:flex md:flex-col md:items-end">
        <span className={`${EYEBROW} ${theme.eyebrow}`}>
          {t('FOOTER.FOLLOW_US')}
        </span>
        <div className="mt-2">
          <SocialMediaIcons
            primaryColor={theme.social}
            justifyContent="start"
          />
        </div>
      </div>
    </div>
  )
}

/** Three platform stats between hairlines. (A rating tile was removed
 *  2026-09-30 — there are no real reviews yet.) */
function TrustBar({ theme }: { theme: FooterTheme }) {
  const { t: tStats } = useTranslation('platform_stats')
  return (
    <div className={`border-y ${theme.rule}`}>
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-3 px-5 py-7 md:gap-x-8 md:px-8 md:py-8">
        <TrustStat
          value={tStats('SPACES')}
          label={tStats('SPACES_LABEL')}
          theme={theme}
        />
        <TrustStat
          value={tStats('CITIES')}
          label={tStats('CITIES_LABEL')}
          theme={theme}
        />
      </div>
    </div>
  )
}

/** Desktop columns + mobile accordions. Both stay in the SSR HTML. */
function LinkSections({
  theme,
  light
}: {
  theme: FooterTheme
  light: boolean
}) {
  const { t } = useTranslation('footer')
  return (
    <div className="mx-auto max-w-7xl px-5 pt-8 pb-10 md:px-8 md:py-14">
      <div className="hidden md:grid md:grid-cols-4 md:gap-x-8 lg:gap-x-12">
        {DESKTOP_COLUMNS.map((column) => (
          <div key={column[0].titleKey} className="space-y-10">
            {column.map((section) => (
              <LinkColumn
                key={section.titleKey}
                section={section}
                theme={theme}
              />
            ))}
          </div>
        ))}
      </div>

      <div className={`border-t md:hidden ${theme.rule}`}>
        {MOBILE_SECTIONS.map((section) => (
          <FooterAccordion
            key={section.titleKey}
            title={t(section.titleKey)}
            light={light}
          >
            <LinkList items={section.items} theme={theme} />
          </FooterAccordion>
        ))}
      </div>
    </div>
  )
}

const ArrowGlyph = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    className="size-4 transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none"
  >
    <path
      d="M4 10h11M11 5.5 15.5 10 11 14.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/** Host acquisition panel — sharp, hairline, Fraunces headline. */
function HostCta({ theme }: { theme: FooterTheme }) {
  const { t } = useTranslation('footer')
  const pill = `inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-7 text-[15px] font-semibold transition-[background-color,border-color,transform] duration-200 active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-2 focus-visible:outline-offset-2 ${theme.ring}`
  return (
    <div className="mx-auto max-w-7xl px-5 pb-12 md:px-8 md:pb-14">
      <div
        className={`relative overflow-hidden border px-6 py-8 md:flex md:items-center md:justify-between md:gap-10 md:px-10 md:py-10 ${theme.panel}`}
      >
        {theme.glow && (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute -top-32 -right-24 size-80 rounded-full ${theme.glow}`}
          />
        )}
        <div className="relative md:max-w-xl">
          <h2
            className={`font-display text-[26px] leading-[1.12] font-medium tracking-[-0.01em] md:text-[34px] ${theme.strong}`}
          >
            {t('FOOTER.CTA_TITLE')}
          </h2>
          <p className={`mt-3 text-[15px] leading-relaxed ${theme.muted}`}>
            {t('FOOTER.CTA_SUBTITLE')}
          </p>
        </div>

        <div className="relative mt-7 flex flex-col gap-3 md:mt-0 md:shrink-0 md:flex-row">
          <Link
            href={RedirectUrls.Anfitrion}
            onClick={() =>
              ga.event('anfitrion_click', { location: 'footer_banner' })
            }
            className={`group ${pill} ${theme.primaryCta}`}
          >
            {t('FOOTER.CTA_PUBLISH')}
            <ArrowGlyph />
          </Link>
          <Link
            href={RedirectUrls.Calculate}
            onClick={() =>
              ga.event('calculator_link_click', { page_type: 'footer' })
            }
            className={`border ${pill} ${theme.secondaryCta}`}
          >
            {t('FOOTER.CTA_CALCULATE')}
          </Link>
        </div>
      </div>
    </div>
  )
}

/** NAP strip — address left, phone + email right (stacked on mobile). */
function ContactStrip({ theme }: { theme: FooterTheme }) {
  const row = `flex min-h-11 gap-2.5 text-[13px] leading-snug decoration-1 underline-offset-4 transition-colors duration-200 hover:underline ${theme.link} ${theme.hover} ${FOCUS} ${theme.ring}`
  const glyph = `size-4 shrink-0 ${theme.icon}`
  return (
    <div className={`border-t ${theme.rule}`}>
      <div className="mx-auto flex max-w-7xl flex-col px-5 py-4 md:flex-row md:items-center md:justify-between md:gap-8 md:px-8 md:py-5">
        <a
          href="https://maps.app.goo.gl/MrfYDy6mQycctBbc7"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            ga.event('nav_link_click', { url: 'maps', surface: 'footer' })
          }
          className={`${row} items-start py-2.5`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className={`mt-px ${glyph}`}
            aria-hidden="true"
          >
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z" />
          </svg>
          <address className="not-italic">
            Av. Manuel Gómez Morín 350-PB 06A
            <br className="md:hidden" />
            <span className="hidden md:inline">, </span>
            Valle del Campestre, 66265 San Pedro Garza García, N.L.
          </address>
        </a>
        <div className="flex flex-col md:flex-row md:items-center md:gap-8">
          <a
            href="tel:+525559301159"
            onClick={() =>
              ga.event('nav_link_click', { url: 'tel', surface: 'footer' })
            }
            className={`${row} items-center`}
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className={glyph}
              aria-hidden="true"
            >
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </svg>
            +52 55 5930 1159
          </a>
          <a
            href="mailto:contacto@spotme.mx"
            onClick={() =>
              ga.event('nav_link_click', {
                url: 'mailto',
                surface: 'footer'
              })
            }
            className={`${row} items-center`}
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className={glyph}
              aria-hidden="true"
            >
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
            contacto@spotme.mx
          </a>
        </div>
      </div>
    </div>
  )
}

/** Copyright + legal links. Legal links wrap (3 items) on mobile. */
function BottomBar({ theme }: { theme: FooterTheme }) {
  const { t } = useTranslation('footer')
  const link = `decoration-1 underline-offset-4 transition-colors duration-200 hover:underline ${theme.hover} ${FOCUS} ${theme.ring}`
  return (
    <div className={`border-t ${theme.rule}`}>
      <div className="mx-auto flex max-w-7xl flex-col-reverse gap-2 px-5 py-5 md:flex-row md:items-center md:justify-between md:gap-6 md:px-8">
        <p
          className={`text-xs leading-relaxed ${theme.muted}`}
          suppressHydrationWarning
        >
          {`© ${new Date().getFullYear()} Northern Land Domain S.A.P.I. de C.V. `}
          <Link
            href="https://www.spotme.mx/"
            className={`font-medium ${theme.link} ${link}`}
          >
            SpotMe
          </Link>
        </p>
        <div className="-mx-2 flex flex-wrap items-center text-xs md:mx-0">
          {LEGAL_LINKS.map((item, i) => (
            <React.Fragment key={item.href}>
              {i > 0 && (
                <span
                  className={`hidden md:inline ${theme.muted}`}
                  aria-hidden="true"
                >
                  ·
                </span>
              )}
              <Link
                href={item.href}
                onClick={() =>
                  ga.event('nav_link_click', {
                    url: item.href,
                    surface: 'footer'
                  })
                }
                className={`inline-flex min-h-11 items-center px-2 md:min-h-0 md:py-1 ${theme.muted} ${link}`}
              >
                {t(item.labelKey)}
              </Link>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Footer
// ---------------------------------------------------------------------------

interface FooterProps {
  light?: boolean
}

function Footer({ light = false }: FooterProps) {
  const theme = light ? LIGHT_THEME : DARK_THEME

  return (
    <footer className={theme.root}>
      {/* ── Blue colophon rule ─────────────────────────────────────────── */}
      <div className="h-0.75 bg-blue-500" aria-hidden="true" />
      <BrandZone theme={theme} />
      <TrustBar theme={theme} />
      <LinkSections theme={theme} light={light} />
      <HostCta theme={theme} />
      <ContactStrip theme={theme} />
      <BottomBar theme={theme} />
    </footer>
  )
}

export default React.memo(Footer)
